import { Box, useToast } from "@chakra-ui/react";
import { dayjs, isValidDateString } from "@equidefi/i18n";
import { useFormik } from "formik";
import { useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import * as Yup from "yup";

import { postalCodeYupSchema } from "@equidefi/portals/helpers/postalCode";
import { US_CITIZEN } from "@equidefi/constants/profile";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useSaveVaultAddress } from "@equidefi/portals/hooks/useVault";
import { PASSPORT, PASSPORT_CARD, offeringIsRegS } from "@equidefi/shared";
import { DateInput, Heading, Text } from "@equidefi/ui";
import { Input } from "@equidefi/ui/forms";

import { UsCitizenRadio } from "@/components/workflow/UsCitizenRadio";
import WorkflowProtection from "@/components/workflow/WorkflowProtection";
import useWorkflowPosition from "@/hooks/useWorkflowPosition";
import { GovernmentIDType } from "@/pages/workflow/address/GovernmentID";
import { IssuingStateSelect } from "@/pages/workflow/address/IssuingState";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";
import { AddressForm } from "./AddressForm";

// Value for 'address_city' approximates the max length that fits on one
// line of an agreement template's custom field: 29 chars per line.
const MAX_CHARACTERS = 29;

export const PASSPORT_ID_TYPES = [PASSPORT.value, PASSPORT_CARD.value];

const idInfoIsRequired = (citizenship, entity_type) => {
  if (entity_type !== "INDIVIDUAL") return false;
  if (citizenship === "US_CITIZEN") return true;
  return false;
};

const addressSchema = Yup.object().shape({
  address_street_1: Yup.string().required("Please enter your street address").nullable(),
  address_street_2: Yup.string().nullable(),
  address_city: Yup.string()
    .required("Please enter your city")
    .nullable()
    .max(MAX_CHARACTERS, "City name should not exceed 29 characters"),
  address_state: Yup.string().required("Please enter your state").nullable(),
  address_postal_code: postalCodeYupSchema("address_country_code", "Please enter your postal code"),
  address_country_code: Yup.string()
    .min(2, "Country code must be 2 characters long")
    .max(2, "Country code must be 2 characters long")
    .nullable()
    .required("Please enter your country"),
  citizenship: Yup.string().nullable().required("Please choose your citizenship"),
  identification_type: Yup.string().when(["citizenship", "entity_type"], {
    is: idInfoIsRequired,
    then: Yup.string().nullable().required("Please select an ID type"),
    otherwise: Yup.string().nullable(),
  }),
  issuing_authority: Yup.string()
    .nullable()
    .when(["citizenship", "entity_type"], {
      is: idInfoIsRequired,
      then: Yup.string()
        .nullable()
        .test("issuing-authority-required", function (value) {
          if (value) return true;
          const isPassport = PASSPORT_ID_TYPES.includes(this.parent.identification_type);
          return this.createError({
            message: isPassport ? "Please select your state of birth" : "Please select the issuing state",
          });
        }),
      otherwise: Yup.string().nullable(),
    }),
  identification_number: Yup.string().when(["citizenship", "entity_type"], {
    is: idInfoIsRequired,
    then: Yup.string().nullable().required("Please enter your ID number"),
    otherwise: Yup.string().nullable(),
  }),
  id_issue_date: Yup.mixed().when(["citizenship", "entity_type"], {
    is: idInfoIsRequired,
    then: Yup.mixed()
      .required("Please enter issue date")
      .test("valid-date", "Please enter a valid date", (value) => isValidDateString(value))
      .test("min-date", "Issue date cannot be this early", (value) => {
        const past = dayjs(new Date(1900, 0, 1));
        return dayjs(value).isSameOrAfter(past);
      })
      .test("max-date", "Issue date cannot be in the future", (value) => {
        return dayjs(value).isSameOrBefore(dayjs());
      })
      .test("year-format", "Please enter a valid year", (value) => {
        return dayjs(value).year() <= 9999;
      }),
    otherwise: Yup.mixed().nullable(),
  }),
  id_expire_date: Yup.mixed().when(["citizenship", "entity_type"], {
    is: idInfoIsRequired,
    then: Yup.mixed()
      .required("Please enter expiration date")
      .test("valid-date", "Please enter a valid date", (value) => isValidDateString(value))
      .test("min-date", "Expiration date must be after issue date", function (value) {
        const issueDate = this.parent.id_issue_date;
        if (!issueDate || !dayjs(issueDate).isValid()) return false;
        return dayjs(value).isAfter(dayjs(issueDate));
      })
      .test("max-date", "Expiration date too far in the future", (value) => {
        return dayjs(value).isSameOrBefore(dayjs().add(50, "years"));
      })
      .test("year-format", "Please enter a valid year", (value) => {
        return dayjs(value).year() <= 9999;
      }),
    otherwise: Yup.mixed().nullable(),
  }),
  birthdate: Yup.mixed()
    .required("Please include your birthdate, and you must be at least 18 years old")
    .test("valid-date", "Please enter a valid date", (value) => isValidDateString(value))
    .test("min-date", "Birth date cannot be this early", (value) => {
      return dayjs(value).isSameOrAfter(dayjs(new Date(1900, 0, 1)));
    })
    .test("max-date", "You must be at least 18 years old", (value) => {
      return dayjs(value).isSameOrBefore(dayjs().subtract(18, "years"));
    })
    .test("year-format", "Please enter a valid year", (value) => {
      return dayjs(value).year() <= 9999;
    }),
});

const IdentityVerification = () => {
  return (
    <Box mb={5}>
      <Text textStyle="h2" fontWeight="bold" mt={10} mb={2}>
        Identity Verification
      </Text>
      <Text textStyle="context">
        Please see "
        <Text
          textStyle="context"
          as={Link}
          textDecor="underline"
          _hover={{ color: "blue.700" }}
          color="blue.600"
          to="https://www.sec.gov/education/capitalraising/building-blocks/accredited-investor"
          rel="noopener noreferrer"
          target="_blank">
          Why Do I Need To Provide My Government ID Information
        </Text>
        " for more details.
      </Text>
    </Box>
  );
};

export const WorkflowAddress = () => {
  useWorkflowPosition(4);
  const { investment_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { offering, user, investment, refetchInvestment } = useWorkflowContext();
  const isOfferingRegS = offeringIsRegS(offering);
  const saveVaultProfile = useSaveVaultAddress(investment_id);

  useDocumentTitle(["Address", offering?.name, investment?.issuer?.name]);
  const toast = useToast();

  const onSubmit = useCallback(
    async ({ entity_type, ...payload }) => {
      const page = location.state?.returnTo ?? "signers";

      try {
        await saveVaultProfile.mutateAsync({
          skip_transition: location.state?.skip_transition,
          user_id: user.id,
          ...payload,
          id_issue_date: payload.id_issue_date ? dayjs(payload.id_issue_date).format("MM/DD/YYYY") : "",
          id_expire_date: payload.id_expire_date ? dayjs(payload.id_expire_date).format("MM/DD/YYYY") : "",
          birthdate: payload.birthdate ? dayjs(payload.birthdate).format("MM/DD/YYYY") : "",
        });
        await refetchInvestment();

        navigate(`/offerings/${offering.slug}/${investment_id}/${page}`);
      } catch (error) {
        console.error(error);
        toast({
          status: "error",
          description: error.response.data.errors.join(". "),
        });
        return;
      }
    },
    [saveVaultProfile, refetchInvestment, navigate, offering?.slug, investment_id, location.state, toast, user?.id],
  );

  const initialValues = {
    address_street_1: investment?.vault?.address_street_1,
    address_street_2: investment?.vault?.address_street_2,
    address_city: investment?.vault?.address_city,
    address_state: investment?.vault?.address_state,
    address_postal_code: investment?.vault?.address_postal_code,
    address_country_code: investment?.vault?.address_country_code,
    citizenship: investment?.vault?.citizenship ?? US_CITIZEN,
    identification_number: investment?.vault?.identification_number,
    id_issue_date:
      investment?.vault?.id_issue_date && dayjs(investment?.vault?.id_issue_date).isValid()
        ? dayjs(investment?.vault?.id_issue_date).format("MM/DD/YYYY")
        : "",
    id_expire_date:
      investment?.vault?.id_expire_date && dayjs(investment?.vault?.id_expire_date).isValid()
        ? dayjs(investment?.vault?.id_expire_date).format("MM/DD/YYYY")
        : "",
    birthdate:
      investment?.vault?.birthdate && dayjs(investment?.vault?.birthdate).isValid()
        ? dayjs(investment?.vault?.birthdate).format("MM/DD/YYYY")
        : "",
    identification_type: investment?.vault?.identification_type,
    issuing_authority: investment?.vault?.issuing_authority,
    entity_type: investment?.vault?.entity_type,
  };

  const formik = useFormik({
    validationSchema: addressSchema,
    onSubmit,
    initialValues,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handleOnAddressSelect = useCallback(
    (value) => {
      const newValues = {
        ...formik.values,
        address_street_1: value.street || "",
        address_street_2: value?.street2,
        address_city: value.city,
        address_state: value.state,
        address_postal_code: value.zipcode,
        address_country_code: value.country,
      };

      formik.setValues(newValues);

      // Trigger validation for all fields
      for (const field of Object.keys(newValues)) {
        formik.setFieldTouched(field, true, false);
      }

      // Run validation
      formik.validateForm(newValues);
    },
    [formik.values, formik],
  );

  return (
    <WorkflowContainer
      title="Investor Details"
      onContinue={formik.handleSubmit}
      buttonProps={{
        isDisabled: !formik.isValid || !formik.values.address_city,
        isLoading: saveVaultProfile.isLoading,
      }}>
      <WorkflowProtection vaultId={investment?.vault_id} />
      <WorkflowForm onSubmit={formik.handleSubmit}>
        {!isOfferingRegS && (
          <UsCitizenRadio
            value={formik.values.citizenship}
            onChange={(value) => {
              formik.setFieldValue("citizenship", value);
            }}
            mt={4}
          />
        )}
        <DateInput
          label="Birthdate (MM/DD/YYYY)"
          name="birthdate"
          mt={0}
          className="fvMask"
          autoFocus
          value={formik.values.birthdate}
          placeholder="MM/DD/YYYY"
          onChange={(date) => {
            formik.setFieldValue("birthdate", date);
          }}
          onBlur={() => {
            formik.setFieldTouched("birthdate", true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              formik.setFieldTouched("birthdate", true);
            }
          }}
          error={formik.errors.birthdate && formik.touched.birthdate ? formik.errors.birthdate : undefined}
          isInvalid={formik.errors.birthdate && formik.touched.birthdate}
          isRequired
        />
        {formik.values.citizenship === "US_CITIZEN" && investment.vault?.entity_type !== "ENTITY" && (
          <>
            <IdentityVerification />
            <GovernmentIDType
              vault={investment.vault}
              className="fvMask"
              setFieldValue={(field, value) => {
                const wasPassport = PASSPORT_ID_TYPES.includes(formik.values.identification_type);
                const isPassport = PASSPORT_ID_TYPES.includes(value);
                if (wasPassport !== isPassport) {
                  formik.setFieldValue("issuing_authority", "");
                }
                formik.setFieldValue(field, value);
              }}
              errors={formik.errors.identification_type}
            />
            <IssuingStateSelect
              className="fvMask"
              setFieldValue={formik.setFieldValue}
              value={formik.values.issuing_authority}
              error={formik.touched.issuing_authority && formik.errors.issuing_authority}
              isPassport={PASSPORT_ID_TYPES.includes(formik.values.identification_type)}
            />
            <Input
              label="Government ID Number"
              isRequired
              className="fvMask"
              error={
                formik.touched.identification_number && formik.errors.identification_number
                  ? formik.errors.identification_number
                  : null
              }
              isInvalid={formik.errors.identification_number && formik.touched.identification_number}
              {...formik.getFieldProps("identification_number")}
              mb={5}
            />
            <DateInput
              label="Government ID Issue Date (MM/DD/YYYY)"
              name="id_issue_date"
              className="fvMask"
              value={formik.values.id_issue_date}
              onChange={(date) => {
                formik.setFieldValue("id_issue_date", date);
              }}
              onBlur={() => {
                formik.setFieldTouched("id_issue_date", true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  formik.setFieldTouched("id_issue_date", true);
                }
              }}
              error={
                formik.errors.id_issue_date && formik.touched.id_issue_date ? formik.errors.id_issue_date : undefined
              }
              isInvalid={formik.errors.id_issue_date && formik.touched.id_issue_date}
              isRequired
            />
            <DateInput
              label="Government ID Expiration Date (MM/DD/YYYY)"
              name="id_expire_date"
              className="fvMask"
              value={formik.values.id_expire_date}
              onChange={(date) => {
                formik.setFieldValue("id_expire_date", date);
              }}
              onBlur={() => {
                formik.setFieldTouched("id_expire_date", true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  formik.setFieldTouched("id_expire_date", true);
                }
              }}
              error={
                formik.errors.id_expire_date && formik.touched.id_expire_date ? formik.errors.id_expire_date : undefined
              }
              isInvalid={formik.errors.id_expire_date && formik.touched.id_expire_date}
              isRequired
            />
          </>
        )}

        <Heading textStyle="h2" mb={0} mt={10} fontWeight="bold">
          Investor's Legal Address
        </Heading>
        <Text mb="2">Enter your legal address, as shown on your Government ID.</Text>
        <AddressForm formik={formik} onAddressSelect={handleOnAddressSelect} />
      </WorkflowForm>
    </WorkflowContainer>
  );
};
